import Phaser from "phaser";
import { EnemyStats, WALK_FRAME_SUFFIXES } from "../config";
import { sound } from "../systems/Sound";
import { impact } from "../systems/Impact";
import { postFx } from "../systems/PostFx";
import { fx } from "../systems/FxPool";
import {
  applySlowTint,
  bossDeathShockwave,
  clearDebuffTint,
  damageNumber,
  deathBurst,
  goldPopup,
  impactSparks
} from "../assets/effects";

export type DamageType = "physical" | "magic" | "pierce";

interface Dot {
  dps: number;
  remainingMs: number;
  source: string;
}

export type MovementProfile = "walk" | "heavy" | "scurry" | "hover" | "roll";

export function getMovementProfile(key: string, isFlying: boolean, isBoss: boolean): MovementProfile {
  if (isFlying || key === "wraith" || key === "void_specter") return "hover";
  if (key === "cart") return "roll";
  if (isBoss || key === "troll" || key === "golem_guardian" || key === "siege_ram") return "heavy";
  if (key === "swarmling" || key === "flame_imp" || key === "goblin" || key === "kobold_rider" || key === "hellhound") return "scurry";
  return "walk";
}

/** Radians of gait phase per ms. One full cycle is two footfalls. */
export function walkCycleRate(profile: MovementProfile): number {
  switch (profile) {
    case "hover":  return 0.0020;
    case "roll":   return 0.0200;
    case "heavy":  return 0.0025;
    case "scurry": return 0.0120;
    default:       return 0.0060;
  }
}

export class Enemy extends Phaser.GameObjects.Container {
  public hp: number;
  public maxHp: number;
  public alive = true;
  public reachedEnd = false;

  private sprite: Phaser.GameObjects.Image;
  private hpBarBg: Phaser.GameObjects.Rectangle;
  private hpBar: Phaser.GameObjects.Rectangle;
  public waypointIdx = 1;

  private slowRemainingMs = 0;
  private slowFactor = 1;
  private dots: Dot[] = [];
  private slowIcon?: Phaser.GameObjects.Rectangle;
  private poisonIcon?: Phaser.GameObjects.Rectangle;
  private hasRevived = false;
  private animTime = Math.random() * 1000;
  private profile: MovementProfile;
  /**
   * Remaining hit-flash time in ms. Ticked in update() rather than driven by a
   * tween + delayedCall, so a boss taking 20 hits/sec allocates nothing and the
   * flash plays back at game speed instead of real time.
   */
  private flashMs = 0;
  private static readonly FLASH_DURATION_MS = 90;
  /** Latch so footstep dust fires once per contact, not every frame of it. */
  private footDown = false;
  /**
   * Walk-cycle frames in cycle order — as many of `WALK_FRAME_SUFFIXES` as this
   * enemy's drawer registered. Floaters, rollers and anything with only a base
   * texture keep the single frame they have always had.
   */
  private readonly walkFrames: string[];
  private frameIdx = 0;

  constructor(
    scene: Phaser.Scene,
    public stats: EnemyStats,
    private waypoints: { x: number; y: number }[]
  ) {
    super(scene, waypoints[0].x, waypoints[0].y);
    this.hp = stats.hp;
    this.maxHp = stats.hp;
    this.profile = getMovementProfile(
      stats.key,
      !!stats.flying,
      !!(stats as { boss?: boolean }).boss
    );

    // 2.5D grounding drop shadow (added first so it renders below the enemy sprite)
    const shadow = scene.add.ellipse(0, stats.radius - 2, stats.radius * 2.2, stats.radius * 0.8, 0x000000, 0.3);
    this.add(shadow);

    const base = `enemy-${stats.key}`;
    // Take frames while they exist. What a frame means is the drawer's business
    // — a stride for legged units, a wing beat or a turning wheel for the ones
    // that never touch the ground — so nothing here needs to know the gait. A
    // hand-painted enemy has only the base texture and simply stays still.
    this.walkFrames = [base];
    for (const suffix of WALK_FRAME_SUFFIXES.slice(1)) {
      if (!scene.textures.exists(`${base}${suffix}`)) break;
      this.walkFrames.push(`${base}${suffix}`);
    }

    this.sprite = scene.add.image(0, 0, base);
    this.add(this.sprite);

    const barW = stats.radius * 2 + 4;
    this.hpBarBg = scene.add.rectangle(0, -stats.radius - 6, barW, 3, 0x000000, 0.7).setOrigin(0.5);
    this.hpBar   = scene.add.rectangle(-barW / 2, -stats.radius - 6, barW, 3, 0x55ff55).setOrigin(0, 0.5);
    this.add(this.hpBarBg);
    this.add(this.hpBar);

    scene.add.existing(this);
  }

  update(_time: number, deltaMs: number): void {
    if (!this.alive) {
      this.sprite.y = 0;
      this.sprite.angle = 0;
      this.sprite.setScale(1);
      return;
    }
    
    // Dynamic 2.5D Y-sorting
    this.setDepth(this.y);

    const dt = deltaMs / 1000;

    // Self-regen (e.g. ice_elemental).
    if (this.stats.regenPctPerSec && this.hp < this.maxHp) {
      this.hp = Math.min(this.maxHp, this.hp + this.maxHp * this.stats.regenPctPerSec * dt);
      const fullW = this.stats.radius * 2 + 4;
      this.hpBar.width = fullW * Math.max(0, this.hp / this.maxHp);
    }

    if (this.dots.length > 0) {
      let totalTick = 0;
      for (const d of this.dots) {
        const tickMs = Math.min(deltaMs, d.remainingMs);
        totalTick += d.dps * (tickMs / 1000);
        d.remainingMs -= deltaMs;
      }
      this.dots = this.dots.filter(d => d.remainingMs > 0);
      if (totalTick > 0) this.applyHpLoss(totalTick, false);
      this.refreshStatusIcons();
    }

    if (this.slowRemainingMs > 0) {
      this.slowRemainingMs -= deltaMs;
    }
    if (this.slowRemainingMs <= 0 && this.slowFactor !== 1) {
      this.slowRemainingMs = 0;
      this.slowFactor = 1;
      if (this.dots.length === 0) clearDebuffTint(this.sprite);
      this.refreshStatusIcons();
    }

    this.tickFlash(deltaMs);
    this.animTime += deltaMs * this.slowFactor;
    this.applyWalkAnimation(this.profile);

    if (!this.alive) return;

    const target = this.waypoints[this.waypointIdx];
    if (!target) { this.reachedEnd = true; this.alive = false; return; }

    const dx = target.x - this.x;
    const dy = target.y - this.y;
    const dist = Math.hypot(dx, dy);
    const step = this.stats.speed * this.slowFactor * dt;

    if (step >= dist) {
      this.x = target.x;
      this.y = target.y;
      this.waypointIdx++;
      if (this.waypointIdx >= this.waypoints.length) {
        this.reachedEnd = true;
        this.alive = false;
      }
    } else {
      this.x += (dx / dist) * step;
      this.y += (dy / dist) * step;
    }
  }

  takeDamage(amount: number, type: DamageType = "physical", ignoreDodge = false, crit = false): number {
    if (!this.alive) return 0;
    // Dodge applies only to single-target hits; splash/AoE bypasses it via
    // `ignoreDodge` so area towers land reliably against dodgy enemies.
    if (!ignoreDodge && this.stats.dodgeChance && Math.random() < this.stats.dodgeChance) {
      damageNumber(this.scene, this.x, this.y - this.stats.radius, 0, "#aaaaaa");
      return 0;
    }
    const r = this.stats.resist;
    let mult = 1;
    if (r) {
      if (type === "physical" && r.physical) mult *= (1 - r.physical);
      if (type === "magic"    && r.magic)    mult *= (1 - r.magic);
      if (type === "pierce"   && r.pierce)   mult *= (1 - r.pierce);
    }
    const dealt = amount * mult;
    if (dealt <= 0) {
      damageNumber(this.scene, this.x, this.y - this.stats.radius, 0, "#88ccff");
      this.flash();
      return 0;
    }
    this.applyHpLoss(dealt, true, crit);
    return dealt;
  }

  /**
   * Shove the enemy back along its own path. Purely cosmetic — waypointIdx is
   * untouched, so a knocked-back enemy walks the same route, it just loses a
   * few pixels of ground. Bosses and heavy units barely budge.
   */
  private knockback(strength: number): void {
    const target = this.waypoints[this.waypointIdx];
    if (!target) return;
    const dx = target.x - this.x;
    const dy = target.y - this.y;
    const d = Math.hypot(dx, dy);
    if (d < 0.001) return;
    const isBoss = !!(this.stats as { boss?: boolean }).boss;
    // Scale resistance with size: a swarmling flies, a siege ram shrugs.
    const mass = isBoss ? 0.12 : Math.max(0.25, 1 - this.stats.radius / 26);
    const push = strength * mass;
    this.x -= (dx / d) * push;
    this.y -= (dy / d) * push;
  }

  private applyHpLoss(amount: number, showFlash: boolean, crit = false): void {
    if (!this.alive) return;
    this.hp -= amount;
    const pct = Math.max(0, this.hp / this.maxHp);
    const fullW = this.stats.radius * 2 + 4;
    this.hpBar.width = fullW * pct;
    this.hpBar.fillColor = pct > 0.5 ? 0x55ff55 : pct > 0.25 ? 0xffcc33 : 0xff4444;

    if (showFlash) {
      this.flash();
      damageNumber(this.scene, this.x, this.y - this.stats.radius, amount, crit ? "#ff9a3c" : "#ffe070", crit);
      // Knockback scales with the bite the hit took out of this enemy, so
      // chip damage nudges and a crit shoves a little harder. Deliberately no
      // hit-stop or camera punch here: crits land far too often for global
      // feedback — freezing the world each time reads as lag, not impact.
      this.knockback((crit ? 4 : 3) * Math.min(1.6, amount / Math.max(1, this.maxHp * 0.12)));
    }

    if (this.hp <= 0) {
      // One-time revive: bounce back to maxHp * reviveOncePct, brief flash.
      if (this.stats.reviveOncePct && !this.hasRevived) {
        this.hasRevived = true;
        this.hp = this.maxHp * this.stats.reviveOncePct;
        const fullW = this.stats.radius * 2 + 4;
        this.hpBar.width = fullW * (this.hp / this.maxHp);
        this.flash();
        damageNumber(this.scene, this.x, this.y - this.stats.radius, 0, "#ffaa44");
        return;
      }
      this.alive = false;
      const isBoss = !!(this.stats as { boss?: boolean }).boss;
      const armored = !isBoss && (this.stats.resist?.physical ?? 0) >= 0.4;
      sound.playDeath(isBoss ? "boss" : armored ? "armor" : "soft");
      deathBurst(this.scene, this.x, this.y, this.stats.color, isBoss ? 26 : 8);
      goldPopup(this.scene, this.x, this.y, this.stats.reward);

      if (isBoss) {
        // Boss deaths get the full treatment: the world drops into slow motion,
        // a shockwave rolls out and the frame blooms gold. This is the single
        // biggest "payoff" moment in a run, so it is allowed to be loud.
        impact.hitStop(80);
        impact.slowMo(0.35, 900);
        postFx.flash(1.0, 0.86, 0.45, 0.42, 620);
        bossDeathShockwave(this.scene, this.x, this.y);
      }
      // Split-on-death: emit a scene event so GameScene can spawn the children
      // using its enemies array + waypoints (Enemy itself doesn't own the path source).
      if (this.stats.splitOnDeath) {
        this.scene.events.emit("enemy:split", {
          type: this.stats.splitOnDeath.type,
          count: this.stats.splitOnDeath.count,
          x: this.x,
          y: this.y,
          waypointIdx: this.waypointIdx,
          waypoints: this.waypoints
        });
      }
    }
  }

  applySlow(factor: number, durationMs: number): void {
    if (!this.alive) return;
    const r = this.stats.resist?.slow ?? 0;
    if (r >= 1) return;
    const effective = 1 - (1 - factor) * (1 - r);
    if (effective < this.slowFactor || this.slowRemainingMs <= 0) {
      this.slowFactor = effective;
    }
    this.slowRemainingMs = Math.max(this.slowRemainingMs, durationMs);
    applySlowTint(this.sprite);
    this.refreshStatusIcons();
  }

  applyDot(dps: number, durationMs: number, sourceKey: string): void {
    if (!this.alive) return;
    const r = this.stats.resist?.poison ?? 0;
    if (r >= 1) return;
    const effDps = dps * (1 - r);
    const existing = this.dots.find(d => d.source === sourceKey);
    if (existing) {
      existing.dps = Math.max(existing.dps, effDps);
      existing.remainingMs = Math.max(existing.remainingMs, durationMs);
    } else {
      this.dots.push({ dps: effDps, remainingMs: durationMs, source: sourceKey });
    }
    applySlowTint(this.sprite);
    this.refreshStatusIcons();
  }

  /** Start a hit flash: red tint + squash punch + sparks. Cheap to spam. */
  private flash(): void {
    this.flashMs = Enemy.FLASH_DURATION_MS;
    this.sprite.setTint(0xff5544);
    impactSparks(this.x, this.y, 3);
  }

  /** Advance the flash window and restore the resting tint when it expires. */
  private tickFlash(deltaMs: number): void {
    if (this.flashMs <= 0) return;
    this.flashMs -= deltaMs;
    if (this.flashMs > 0) return;
    this.flashMs = 0;
    // Debuff tints outlive the flash, so re-apply rather than blanket-clear.
    if (this.slowRemainingMs > 0 || this.dots.length > 0) applySlowTint(this.sprite);
    else clearDebuffTint(this.sprite);
  }

  private refreshStatusIcons(): void {
    const slowed = this.slowRemainingMs > 0;
    const poisoned = this.dots.length > 0;

    if (slowed && !this.slowIcon) {
      this.slowIcon = this.scene.add.rectangle(-6, -this.stats.radius - 12, 4, 4, 0x88ccff).setOrigin(0.5);
      this.add(this.slowIcon);
    } else if (!slowed && this.slowIcon) {
      this.slowIcon.destroy();
      this.slowIcon = undefined;
    }

    if (poisoned && !this.poisonIcon) {
      this.poisonIcon = this.scene.add.rectangle(0, -this.stats.radius - 12, 4, 4, 0x6abf3a).setOrigin(0.5);
      this.add(this.poisonIcon);
    } else if (!poisoned && this.poisonIcon) {
      this.poisonIcon.destroy();
      this.poisonIcon = undefined;
    }
  }

  /**
   * Walk rig.
   *
   * The old version was a single sine wave on Y plus a sine on rotation, which
   * reads as "hovering and wobbling" rather than walking — a real gait is not
   * symmetrical. This drives one normalised cycle phase (0..1) through four
   * beats: contact, passing, contact, passing. On each contact the body
   * squashes and drops, between contacts it stretches and rises, and the lean
   * shifts with the weight rather than oscillating around centre.
   *
   * Everything is written once per frame into y / angle / scale, so the hit
   * punch can ride on top without a competing tween.
   */
  private applyWalkAnimation(profile: MovementProfile): void {
    const t = this.animTime;

    // Cycle speed per profile (radians of phase per ms).
    const rate = walkCycleRate(profile);

    // phase 0..1 across one full two-step cycle.
    const phase = ((t * rate) / (Math.PI * 2)) % 1;
    // Two footfalls per cycle: step 0..1 within the current stride.
    const step = (phase * 2) % 1;
    // 1 at contact, 0 at mid-stride. Sharp at contact, soft between.
    const contact = Math.pow(Math.cos(step * Math.PI * 2) * 0.5 + 0.5, 1.6);
    // Weight shift across the whole two-step cycle: leans one way on the first
    // stride and the other on the second, so the gait is asymmetric like a real
    // walk. Deliberately a continuous sine over `phase` rather than a
    // per-stride +1/-1 sign — that flipped the lean ~6 degrees in a single
    // frame at each footfall, which read as a jitter rather than a shift.
    const lead = Math.sin(phase * Math.PI * 2);

    let bob: number;
    let lean: number;
    let squash: number;

    switch (profile) {
      case "hover": {
        // Floating: no footfalls at all, just a slow buoyant drift.
        bob = Math.sin(t * 0.004) * 4;
        lean = Math.sin(t * 0.0028) * 1.2;
        squash = 0;
        break;
      }
      case "roll": {
        // Wheels on a rutted road: fast, tiny, no squash.
        bob = Math.sin(t * 0.04) * 0.6;
        lean = Math.sin(t * 0.021) * 0.5;
        squash = 0;
        break;
      }
      case "heavy": {
        // Slow deliberate stomps. Drops hard onto each foot and takes its
        // weight, so contact dominates the pose.
        bob = -5 * (1 - contact) - 1.5;
        lean = lead * 2.1;
        squash = contact * 0.085;
        break;
      }
      case "scurry": {
        // Quick and light: barely touches the ground, minimal squash.
        bob = -2.6 * (1 - contact);
        lean = lead * 3.0 + Math.sin(step * Math.PI * 2) * 0.8;
        squash = contact * 0.035;
        break;
      }
      case "walk":
      default: {
        // Standard gait: clear rise between steps, definite landing.
        bob = -3.6 * (1 - contact);
        lean = lead * 1.9;
        squash = contact * 0.055;
        break;
      }
    }

    this.sprite.y = bob;
    this.sprite.angle = lean;

    // Walk frame straight off the cycle phase. Frame 0 and the frame at half
    // phase sit exactly on the two contacts, which is where the dust below
    // fires — so a six-frame gait keeps landing on the beat a two-frame one
    // did. Only write the texture when the index actually changes.
    if (this.walkFrames.length > 1) {
      const idx = Math.floor(phase * this.walkFrames.length) % this.walkFrames.length;
      if (idx !== this.frameIdx) {
        this.frameIdx = idx;
        this.sprite.setTexture(this.walkFrames[idx]);
      }
    }

    // Footfall dust: fire once per contact, on the frame the foot lands.
    if (squash > 0 && contact > 0.86 && !this.footDown) {
      this.footDown = true;
      this.emitFootstep();
    } else if (contact < 0.5) {
      this.footDown = false;
    }

    // Hit punch rides on top of the gait as a single scale write, so it can
    // never fight the base scale the way a parallel tween used to.
    // sin() peaks at the middle of the flash window = pop then settle.
    const p = this.flashMs > 0 ? Math.sin((this.flashMs / Enemy.FLASH_DURATION_MS) * Math.PI) : 0;
    this.sprite.setScale(
      1 + 0.14 * p + squash,
      1 - 0.12 * p - squash
    );
  }

  /**
   * Small dust puff under the foot on contact. Pooled, and skipped for small
   * or slowed units so a swarm wave doesn't turn the path into a dust storm.
   */
  private emitFootstep(): void {
    if (this.stats.radius < 11) return;
    if (this.slowFactor < 1) return;
    const heavy = this.profile === "heavy";
    fx.puff(
      this.x + (Math.random() - 0.5) * this.stats.radius,
      this.y + this.stats.radius - 2,
      0xb8a888,
      heavy ? 4.5 : 3,
      heavy ? 0.34 : 0.20
    );
  }
}
